import PropTypes from "prop-types"
import { domToReact } from "html-react-parser"
import parse from "html-react-parser"
import Image from "next/image"
import Link from "next/link"
import clsx from "clsx"
import { Text } from "../text/Text"
import { Heading } from "../heading/Heading"

const STANDARD_HTML_TAGS = new Set([
	"a",
	"abbr",
	"b",
	"blockquote",
	"br",
	"caption",
	"cite",
	"code",
	"dd",
	"del",
	"div",
	"dl",
	"dt",
	"em",
	"figcaption",
	"figure",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"hr",
	"i",
	"iframe",
	"img",
	"ins",
	"li",
	"mark",
	"ol",
	"p",
	"pre",
	"q",
	"s",
	"small",
	"span",
	"strong",
	"sub",
	"sup",
	"table",
	"tbody",
	"td",
	"tfoot",
	"th",
	"thead",
	"tr",
	"u",
	"ul",
])

const isTagNode = (domNode) => domNode?.type === "tag" && typeof domNode.name === "string"

const normalizeHtml = (html) => {
	if (html == null) return ""
	if (typeof html === "string") return html
	if (typeof html === "object") {
		if (typeof html["#text"] === "string") return html["#text"]
		if (typeof html.processed === "string") return html.processed
		if (typeof html.value === "string") return html.value
	}
	return String(html)
}

const sanitizeOfficeHtml = (html) =>
	normalizeHtml(html)
		.replace(/<\/?[a-z0-9]+:[^>]*>/gi, "")
		.replace(/<\/?\?xml[^>]*>/gi, "")

const unwrapNodeChildren = (domNode, options) => domToReact(domNode.children, options)

export function Wysiwyg({ html, as, prose, className, textVariant, ...rest }) {
	// usecase: Wysiwyg inside of p ==> wrong dom nesting
	const AsComponent = as === "p" ? "div" : (as ?? "div")

	// prose should be optional. e.g, form valadiation messages are not prose
	const finalClassName = clsx(prose && "prose", className)

	const options = {
		replace: (domNode) => {
			if (!isTagNode(domNode)) return undefined

			const tagName = domNode.name.toLowerCase()

			// Unwrap Microsoft Office / Word and other non-standard tags.
			if (tagName.includes(":") || !STANDARD_HTML_TAGS.has(tagName)) {
				return unwrapNodeChildren(domNode, options)
			}

			if (tagName === "img") {
				const { src, alt, width, height } = domNode.attribs
				const parsedWidth = Number.parseInt(width, 10) || 800
				const parsedHeight = Number.parseInt(height, 10) || 600
				const imageSrc =
					src?.startsWith("http://") || src?.startsWith("https://")
						? src
						: `${process.env.NEXT_PUBLIC_DRUPAL_BASE_URL}${src?.startsWith("/") ? src : `/${src || ""}`}`

				return (
					<Image
						src={imageSrc}
						width={parsedWidth}
						height={parsedHeight}
						alt={alt || "wysiwyg image"}
						className="max-w-full object-cover"
						style={{ width: "100%", height: "auto" }}
					/>
				)
			}

			if (/^h[1-6]$/.test(tagName)) {
				const level = Number(tagName.slice(1))
				return (
					<Heading level={level} className="mt-6">
						{unwrapNodeChildren(domNode, options)}
					</Heading>
				)
			}

			if (tagName === "ul" || tagName === "ol") {
				const Tag = tagName
				const listClassName = domNode.attribs?.class ?? "custom-list"
				return (
					<Tag className={clsx(listClassName)}>{unwrapNodeChildren(domNode, options)}</Tag>
				)
			}

			if (tagName === "p") {
				const paragraphClassName = domNode?.attribs?.class?.includes("-align-")
					? domNode.attribs.class.replace("-align-", "-")
					: domNode.attribs?.class
				return (
					<Text as="p" variant={textVariant} className={paragraphClassName}>
						{unwrapNodeChildren(domNode, options)}
					</Text>
				)
			}

			if (tagName === "a") {
				const { href, class: linkClassName } = domNode.attribs

				// Contributed HTML may contain anchors without href (named anchors,
				// text pasted from other tools): next/link throws on an undefined href
				// and the whole page rendered as a 500. Keep the text and the anchor
				// target (id / name), drop the link.
				if (!href) {
					return (
						<span
							id={domNode.attribs.id || domNode.attribs.name}
							className={linkClassName}
						>
							{unwrapNodeChildren(domNode, options)}
						</span>
					)
				}

				return (
					<Link href={href} className={linkClassName}>
						{unwrapNodeChildren(domNode, options)}
					</Link>
				)
			}

			return undefined
		},
	}

	return (
		<AsComponent className={clsx(finalClassName, "wysiwyg")} {...rest}>
			{parse(sanitizeOfficeHtml(html), options)}
		</AsComponent>
	)
}

Wysiwyg.propTypes = {
	prose: PropTypes.bool,
	as: PropTypes.element,
}
